import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import InventoryAPI from "../../services/inventoryService";

export const ProductSelectionModal = ({
  show,
  onHide,
  service,
  installerId,
  onSelectProduct
}) => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState(
    service?.recommended_product_id || null
  );
  const [error, setError] = useState(null);

  // Fetch products when modal opens
  useEffect(() => {
    if (show && service) {
      loadProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, service]);

  // Reset selection when service changes
  useEffect(() => {
    const productId = service?.recommended_product_id || null;
    setSelectedProductId(productId);

    // If there's an existing selection, try to set the brand
    if (productId && products.length > 0) {
      const existingProduct = products.find(p => (p.product_id || p.id) === productId);
      if (existingProduct) {
        setSelectedBrand(existingProduct.brand || '');
      }
    } else {
      setSelectedBrand('');
    }
  }, [service, products]);

  // Filter products when brand changes
  useEffect(() => {
    if (selectedBrand) {
      const filtered = products.filter(p => p.brand === selectedBrand);
      setFilteredProducts(filtered);

      // If current selection is not in filtered list, clear it
      if (selectedProductId) {
        const stillValid = filtered.some(p => (p.product_id || p.id) === selectedProductId);
        if (!stillValid) {
          setSelectedProductId(null);
        }
      }
    } else {
      setFilteredProducts([]);
      setSelectedProductId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand, products]);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all products for this installer
      const allProducts = await InventoryAPI.getProducts(installerId);

      // Filter by service category name (products have category_name field)
      const categoryName = service.service_category;
      const categoryProducts = (allProducts || []).filter(product =>
        product.category_name === categoryName
      );

      // Sort by brand then name
      const sorted = categoryProducts.sort((a, b) => {
        const brandA = (a.brand || '').toLowerCase();
        const brandB = (b.brand || '').toLowerCase();
        if (brandA !== brandB) return brandA.localeCompare(brandB);

        const nameA = (a.product_name || a.name || '').toLowerCase();
        const nameB = (b.product_name || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      // Extract unique brands
      const uniqueBrands = [...new Set(sorted.map(p => p.brand).filter(Boolean))].sort();

      setProducts(sorted);
      setBrands(uniqueBrands);

      // If there's an existing selection, set the brand
      const existingProductId = service?.recommended_product_id;
      if (existingProductId) {
        const existingProduct = sorted.find(p => (p.product_id || p.id) === existingProductId);
        if (existingProduct) {
          setSelectedBrand(existingProduct.brand || '');
        }
      }
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId ? parseInt(productId, 10) : null);
  };

  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    setSelectedProductId(null);
  };

  const handleClearSelection = () => {
    setSelectedBrand('');
    setSelectedProductId(null);
  };

  const handleApply = () => {
    const selectedProduct = products.find(p =>
      (p.product_id || p.id) === selectedProductId
    );

    onSelectProduct(
      service._uid || service.service_id,
      selectedProductId,
      selectedProduct ? (selectedProduct.product_name || selectedProduct.name) : null,
      selectedProduct ? selectedProduct.brand : null
    );
    onHide();
  };

  const getSelectedProductDisplay = () => {
    if (!selectedProductId) return null;
    const product = products.find(p => (p.product_id || p.id) === selectedProductId);
    if (!product) return null;
    const name = product.product_name || product.name;
    return product.brand ? `${product.brand} - ${name}` : name;
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-sharp fa-regular fa-cube mr-2"></i>
          Select Product
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <small className="text-muted">Service:</small>
          <div className="font-weight-bold">{service?.service_category ? `${service.service_category} - ` : ''}{service?.description}</div>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="mr-2" />
            Loading products...
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : products.length === 0 ? (
          <div className="alert alert-info">
            <i className="fa-sharp fa-regular fa-info-circle mr-2"></i>
            No products defined for this category yet.
            <br />
            <small className="text-muted">
              Add products in the Inventory section to enable selection here.
            </small>
          </div>
        ) : (
          <Form>
            <Form.Group>
              <Form.Label>Brand</Form.Label>
              <Form.Control
                as="select"
                value={selectedBrand}
                onChange={(e) => handleBrandChange(e.target.value)}
              >
                <option value="">-- Select a brand --</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group>
              <Form.Label>Product</Form.Label>
              <Form.Control
                as="select"
                value={selectedProductId || ''}
                onChange={(e) => handleProductSelect(e.target.value)}
                disabled={!selectedBrand}
              >
                <option value="">-- Select a product --</option>
                {filteredProducts.map(product => {
                  const productId = product.product_id || product.id;
                  const productName = product.product_name || product.name;

                  return (
                    <option key={productId} value={productId}>
                      {productName}
                    </option>
                  );
                })}
              </Form.Control>
              {!selectedBrand && (
                <Form.Text className="text-muted">
                  Select a brand first
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        )}

        {selectedProductId && (
          <div className="bg-light p-3 mt-3 rounded">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted d-block">Selected Product:</small>
                <span className="font-weight-bold">{getSelectedProductDisplay()}</span>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {selectedProductId && (
          <Button
            variant="outline-danger"
            onClick={handleClearSelection}
          >
            Clear Selection
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={loading}
        >
          {selectedProductId ? 'Apply Selection' : 'No Product'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductSelectionModal;
